import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Box, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { learnLearningPathListView } from "../../routes/LearningCenterRoutes";
import ReactTooltip from "react-tooltip";

const eventType = {
  assignment: "assignment"
};

const useStyles = makeStyles(() => ({
  tooltip: {
    boxShadow: "0 3px 7px rgba(0, 0, 0, 0.3);",
    opacity: "1 !important",
    pointerEvents: "auto !important",
    "&:hover": {
      visibility: "visible !important",
      opacity: "1 !important"
    },
    zIndex: 15
  }
}));

const CalendarTooltips = ({ children, delayHide, place, events }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tooltipList, setTooltipList] = useState([]);
  const { t } = useTranslation();

  const getTooltipInfo = event => {
    const eventStartTime = moment(event.start);
    const eventEndTime = moment(event.end);
    const momentFormat = event.allDay ? "ll" : "lll";

    const tooltipInfo = {
      eventId: event.id,
      title: event.title,
      time: event.time,
      type: event.type,
      start: eventStartTime.format(momentFormat),
      end: event.allDay ? "All-day" : eventEndTime.format(momentFormat),
      description: event.description,
      allDay: event.allDay,
      placeLabel: event.placeLabel,
      isIlt: event.isIlt
    };
    return tooltipInfo;
  };

  const onMouseEnter = info => {
    if (info && !info.el.getAttribute("data-tip")) {
      info.el.setAttribute("data-tip", true);
      info.el.setAttribute("data-for", `tooltip${info.event.id}`);

      ReactTooltip.rebuild();
    }
  };

  useEffect(() => {
    if (events) {
      setTooltipList(events.map(getTooltipInfo));
    }
  }, [events]);

  return (
    <>
      {tooltipList.map(item => (
        <ReactTooltip
          key={item.eventId}
          id={"tooltip" + item.eventId}
          type="light"
          effect="solid"
          className={classes.tooltip}
          place={place}
          delayHide={delayHide}
        >
          <Box px={0.5} py={1}>
            <Typography variant="subtitle1" sx={{ wordWrap: "break-word" }}>
              {item.type === "assignment"
                ? t("learningCenterCalendar.assignmentCalendarTitle", {
                    course_name: item.title
                  })
                : item.title}
            </Typography>
            {!!item.description && (
              <Typography sx={{ wordWrap: "break-word", maxWidth: 400, pt: 1 }}>
                {item.description}
              </Typography>
            )}
            <Box mt={2} display="flex">
              <Box mr={2} display="flex" alignItems="center">
                <EventRoundedIcon color="disabled" />
              </Box>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="textSecondary" noWrap>
                  {item.type === "assignment" && !item.allDay && !item.isIlt
                    ? item.start
                    : `${item.start} - ${item.end}`}
                </Typography>
              </Box>
            </Box>
            {item.placeLabel && (
              <Box mt={1} display="flex">
                <Box mr={2} display="flex" alignItems="center">
                  <LocationOnOutlinedIcon color="disabled" />
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    {item.placeLabel}
                  </Typography>
                </Box>
              </Box>
            )}
            {item.type === eventType.assignment && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mt={2}
              >
                <Link
                  component="button"
                  onClick={() => dispatch(push(learnLearningPathListView))}
                  variant="subtitle2"
                  noWrap
                >
                  {t("learningCenterCalendar.viewMyLearningPathAssignment")}
                </Link>
              </Box>
            )}
          </Box>
        </ReactTooltip>
      ))}
      {children({ onMouseEnter })}
    </>
  );
};

CalendarTooltips.propTypes = {
  /**
   * Should return an instance of the Calendar component that accepts the onMouseEnter method
   * which is called when the user hovers over a calendar event
   **/
  children: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      time: PropTypes.string,
      start: PropTypes.string,
      end: PropTypes.string,
      description: PropTypes.string,
      placeLabel: PropTypes.string,
      isIlt: PropTypes.bool
    })
  )
};

export default CalendarTooltips;
